import { defineStore } from "pinia";
import * as util from "@/js/utilities";

import { isRestrictedCore, useCoresStore } from "@/stores/cores";
import { PreferenceWriteOptions, useUserStore } from "@/stores/user";
import { isLocalMountConfig, LocalMountConfig } from "@/types/api/dataSources";

import { base64Decode, base64Encode } from "@/js/utilities";
import { useDataSourcesStore } from "./dataSources";
import { RemoteAccessState } from "@/types/general";
import { UserPreferences } from "@/types/user";
import { monitoringCaptureError } from "@/plugins/monitoring";
import { saveOrRevert } from "@/js/optimisticWrites";

import { EditorTheme, themes as monacoThemes } from "@/js/editor";

import fallbackEditorCode from "@/assets/example_loadFromFile.c.js";
const fallbackEditorTheme: EditorTheme = "solarized-light";
const fallbackEditorLayout = "vertical";
const fallbackVariableViewerVisibility = false;
const fallbackExecutionCoreID = "cor_e7eec4dce06f5a03a1b5418e0c5cbd7e"; /* C0Pro-S+ */
const maxArgLength = 100_000;

function isEditorLayout(value: unknown): value is "vertical" | "horizontal" {
	return value === "vertical" || value === "horizontal";
}

/*
 * mountConfig can be undefined | null | LocalMountConfig
 * `undefined` => `mountConfig` hasn't been set
 * 	- should fallback to default.
 *  - If default not defined, fallback to `null` ?
 * `null` => user has removed the mount config
 * 	- honour `null` and run code without data source mounting
 * `LocalMountConfig` => user (or system) has set the mount config
 * 	- honour `LocalMountConfig` and run code without data source mounting
 */

type StoreState = {
	editorThemeID: undefined | string;
	savedEditorThemeID: undefined | string;
	variableViewerVisibility: boolean;
	editorSourceCode: undefined | string;
	editorCore: undefined | string;
	coreUpdateStatus: RemoteAccessState;
	variableViewerVisibilityUpdateStatus: RemoteAccessState;
	themeUpdateStatus: RemoteAccessState;
	dataSourceUpdateStatus: RemoteAccessState;
	runArgumentsUpdateStatus: RemoteAccessState;
	compilationFlagsUpdateStatus: RemoteAccessState;
	mountConfig: undefined | null | LocalMountConfig;
	codeSyncFrequency: number; //How often to check for code changes before synchronising with remote
	editorRunArgs: string;
	editorCompilationFlags: string;
	codeLanguage: string;
	lastBuildID: undefined | string;
	editorLayout: "vertical" | "horizontal";
};

export const useEditorStore = defineStore("editor", {
	state: (): StoreState => ({
		editorThemeID: undefined,
		savedEditorThemeID: undefined,
		variableViewerVisibility: fallbackVariableViewerVisibility,
		editorSourceCode: undefined,
		editorRunArgs: "",
		editorCompilationFlags: "",
		editorCore: undefined,
		coreUpdateStatus: { loading: false, error: false, message: "" },
		variableViewerVisibilityUpdateStatus: { loading: false, error: false, message: "" },
		themeUpdateStatus: { loading: false, error: false, message: "" },
		dataSourceUpdateStatus: { loading: false, error: false, message: "" },
		runArgumentsUpdateStatus: { loading: false, error: false, message: "" },
		compilationFlagsUpdateStatus: { loading: false, error: false, message: "" },
		mountConfig: undefined,
		codeSyncFrequency: 5_000,
		codeLanguage: "",
		lastBuildID: undefined,
		editorLayout: "vertical",
	}),
	getters: {
		editorCoreObject: (state) => {
			if (!state.editorCore) {
				return undefined;
			}
			const coresStore = useCoresStore();

			let core = coresStore.getCoreByID(state.editorCore, coresStore.filteredCoresList);
			if (!core) {
				core = coresStore.defaultCore;
				console.warn(
					`(Editor initialisation) Specified editor core not found: ${state.editorCore}. returning default: ${core?.CoreID}`,
				);
			}

			return core;
		},
		themeList: () => {
			// eslint-disable-next-line prefer-const
			let monacoThemesList = new Array(Object.keys(monacoThemes).length);
			let index = 0;
			for (const key in monacoThemes) {
				if (Object.hasOwnProperty.call(monacoThemes, key)) {
					const element = monacoThemes[key];
					monacoThemesList[index++] = {
						name: element,
						key: key,
					};
				}
			}
			return monacoThemesList;
		},
		rawThemeList: () => monacoThemes,
		getLastBuild: (state) => {
			if (state.lastBuildID) {
				return state.lastBuildID;
			}
			return sessionStorage.getItem("signaloid-editor-last-build") ?? undefined;
		},
	},
	actions: {
		/*
		 * Editor theme
		 */
		setLastBuild(buildID: string) {
			sessionStorage.setItem("signaloid-editor-last-build", buildID);
			this.lastBuildID = buildID;
		},
		resetLastBuild() {
			sessionStorage.removeItem("signaloid-editor-last-build");
			this.lastBuildID = undefined;
		},
		async syncPreferredTheme() {
			const authUserStore = useUserStore();
			const userPreferences = await authUserStore.getCurrentUserPreferences();
			this.savedEditorThemeID = userPreferences?.Editor_Theme ?? fallbackEditorTheme;
		},
		async setPreferredTheme(themeID: undefined | string) {
			const previousSavedThemeID = this.savedEditorThemeID;
			this.savedEditorThemeID = themeID;
			await saveOrRevert(
				() => this.setEditorTheme(themeID, true, { shouldReportFailure: true }),
				() => this.savedEditorThemeID,
				() =>
					(this.savedEditorThemeID =
						useUserStore().preferences?.Editor_Theme ?? previousSavedThemeID ?? fallbackEditorTheme),
			);
		},
		setToPreferredTheme() {
			this.setEditorTheme(this.savedEditorThemeID, true, { shouldReportFailure: true });
		},
		setThemeToDefault(syncWithRemote: boolean) {
			this.setEditorTheme(fallbackEditorTheme, syncWithRemote);
		},
		async setEditorTheme(
			themeID: undefined | null | string,
			syncWithRemote: boolean,
			writeOptions: PreferenceWriteOptions = {},
		): Promise<boolean> {
			this.themeUpdateStatus.loading = true;
			const previousThemeID = this.editorThemeID;

			// check if theme ID is valid
			this.editorThemeID = themeID && monacoThemes[themeID] ? themeID : fallbackEditorTheme;
			// localStorage.setItem("signaloid-editor-theme", this.editorThemeID);

			let success = true;
			if (syncWithRemote) {
				const authUserStore = useUserStore();
				success = await saveOrRevert(
					() =>
						authUserStore.syncUserPreferenceWithRemote({ Editor_Theme: this.editorThemeID }, writeOptions),
					() => this.editorThemeID,
					() =>
						(this.editorThemeID =
							authUserStore.preferences?.Editor_Theme ?? previousThemeID ?? fallbackEditorTheme),
				);
			}
			this.themeUpdateStatus.loading = false;
			return success;
		},

		/*
		 * Editor Layout
		 */
		toggleEditorLayout(syncWithRemote: boolean, writeOptions: PreferenceWriteOptions = {}) {
			if (this.editorLayout === "vertical") {
				this.setEditorLayout("horizontal", syncWithRemote, writeOptions);
			} else {
				this.setEditorLayout("vertical", syncWithRemote, writeOptions);
			}
		},
		async setEditorLayout(layout: string, syncWithRemote: boolean, writeOptions: PreferenceWriteOptions = {}) {
			const previousLayout = this.editorLayout;
			// check if theme ID is valid
			this.editorLayout = isEditorLayout(layout) ? layout : fallbackEditorLayout;

			// localStorage.setItem("editor-layout", this.editorLayout);
			// localStorage.setItem("editor-layout-ratio", this.editorLayout);

			if (syncWithRemote) {
				const authUserStore = useUserStore();
				await saveOrRevert(
					() =>
						authUserStore.syncUserPreferenceWithRemote({ Editor_Layout: this.editorLayout }, writeOptions),
					() => this.editorLayout,
					() => {
						const storedLayout = authUserStore.preferences?.Editor_Layout;
						this.editorLayout = isEditorLayout(storedLayout) ? storedLayout : previousLayout;
					},
				);
			}
		},

		/*
		 * Variable Viewer
		 */
		toggleVariableViewerVisibility(syncWithRemote: boolean, writeOptions: PreferenceWriteOptions = {}) {
			this.setVariableViewerVisibility(!this.variableViewerVisibility, syncWithRemote, writeOptions);
		},
		async setVariableViewerVisibility(
			visibility: boolean,
			syncWithRemote: boolean,
			writeOptions: PreferenceWriteOptions = {},
		) {
			this.variableViewerVisibilityUpdateStatus.loading = true;
			const previousVisibility = this.variableViewerVisibility;
			this.variableViewerVisibility = visibility ?? fallbackVariableViewerVisibility;

			if (syncWithRemote) {
				const authUserStore = useUserStore();
				await saveOrRevert(
					() =>
						authUserStore.syncUserPreferenceWithRemote(
							{ Editor_Layout_VariableViewer: String(this.variableViewerVisibility) },
							writeOptions,
						),
					() => this.variableViewerVisibility,
					() => {
						const storedVisibility = authUserStore.preferences?.Editor_Layout_VariableViewer;
						this.variableViewerVisibility =
							storedVisibility === undefined ? previousVisibility : storedVisibility === "true";
					},
				);
			}
			this.variableViewerVisibilityUpdateStatus.loading = false;
		},

		/*
		 * Editor Core
		 */
		async setEditorCore(coreID: string, syncWithRemote: boolean, writeOptions: PreferenceWriteOptions = {}) {
			this.coreUpdateStatus.loading = true;
			const coresStore = useCoresStore();

			await util.waitFor(() => coresStore.loadingCores === false);
			const coreInCoresStore = coresStore.getCoreByID(coreID, coresStore.filteredCoresList);
			const previousCore = this.editorCore;
			if (coreInCoresStore) {
				this.editorCore = coreInCoresStore.CoreID;
			} else {
				const defaultCore = coresStore.defaultCore;
				if (defaultCore?.CoreID) {
					console.warn(
						`Specified editor core not found: ${coreID}. Resetting to default: ${defaultCore.CoreID}`,
					);
					this.editorCore = defaultCore.CoreID;
				} else {
					console.error(`Specified editor core not found: ${coreID} and no default core available`);
					this.editorCore = undefined;
				}
			}

			if (syncWithRemote && this.editorCore) {
				const authUserStore = useUserStore();
				await saveOrRevert(
					() =>
						authUserStore.syncUserPreferenceWithRemote(
							{ Editor_Execution_Core: this.editorCore },
							writeOptions,
						),
					() => this.editorCore,
					() => (this.editorCore = authUserStore.preferences?.Editor_Execution_Core ?? previousCore),
				);
			}
			this.coreUpdateStatus.loading = false;
		},

		/*
		 * Editor Cmd Line Arguments
		 */
		async setEditorArgs(args: string, syncWithRemote: boolean, writeOptions: PreferenceWriteOptions = {}) {
			this.runArgumentsUpdateStatus.loading = true;
			const previousRunArgs = this.editorRunArgs;
			this.editorRunArgs = args.slice(0, maxArgLength);

			// localStorage.setItem("editor-runArguments", args);
			if (syncWithRemote) {
				const authUserStore = useUserStore();
				await saveOrRevert(
					() =>
						authUserStore.syncUserPreferenceWithRemote(
							{ Editor_Execution_Arguments: this.editorRunArgs },
							writeOptions,
						),
					() => this.editorRunArgs,
					() =>
						(this.editorRunArgs = authUserStore.preferences?.Editor_Execution_Arguments ?? previousRunArgs),
				);
			}
			this.runArgumentsUpdateStatus.loading = false;
		},

		/*
		 * Editor Compilation Flags
		 */
		async setEditorCompilationFlags(
			flags: string,
			syncWithRemote: boolean,
			writeOptions: PreferenceWriteOptions = {},
		) {
			this.compilationFlagsUpdateStatus.loading = true;
			const previousCompilationFlags = this.editorCompilationFlags;
			this.editorCompilationFlags = flags.slice(0, maxArgLength);

			if (syncWithRemote) {
				const authUserStore = useUserStore();
				await saveOrRevert(
					() =>
						authUserStore.syncUserPreferenceWithRemote(
							{ Editor_Compilation_Flags: this.editorCompilationFlags },
							writeOptions,
						),
					() => this.editorCompilationFlags,
					() =>
						(this.editorCompilationFlags =
							authUserStore.preferences?.Editor_Compilation_Flags ?? previousCompilationFlags),
				);
			}
			this.compilationFlagsUpdateStatus.loading = false;
		},

		/*
		 * Editor Mount Configs
		 */
		async setEditorMountConfig(
			mountConfig: null | undefined | LocalMountConfig,
			syncWithRemote: boolean,
			writeOptions: PreferenceWriteOptions = {},
		) {
			this.dataSourceUpdateStatus.loading = true;
			const previousMountConfig = this.mountConfig;

			if (isLocalMountConfig(mountConfig)) {
				// valid mount config
				this.mountConfig = mountConfig;
			} else if (mountConfig === null) {
				// user has removed mounting
				this.mountConfig = null;
			} else {
				// unknown mount config, set to default
				const dataSourcesStore = useDataSourcesStore();
				this.mountConfig =
					(await dataSourcesStore.getGlobalDefaultMountConfig()) ??
					(await dataSourcesStore.getFallbackMountConfig());
			}

			// localStorage.setItem("signaloid-editor-mountConfig", JSON.stringify(this.mountConfig));
			if (syncWithRemote) {
				const authUserStore = useUserStore();
				await saveOrRevert(
					() =>
						authUserStore.syncUserPreferenceWithRemote(
							{ Editor_Execution_DataSources: JSON.stringify(this.mountConfig) },
							writeOptions,
						),
					() => this.mountConfig,
					() => {
						const storedMountConfig = util.parseMountConfigString(
							authUserStore.preferences?.Editor_Execution_DataSources,
						);
						this.mountConfig = storedMountConfig !== undefined ? storedMountConfig : previousMountConfig;
					},
				);
			}
			this.dataSourceUpdateStatus.loading = false;
		},

		/*
		 * Editor Source Code
		 */
		async resetEditorSourceCode() {
			this.editorSourceCode = fallbackEditorCode;

			const encodedCodeString = base64Encode(this.editorSourceCode);
			sessionStorage.setItem("signaloid-editor-code", encodedCodeString);
			const authUserStore = useUserStore();
			await authUserStore.syncUserPreferenceWithRemote(
				{ Editor_SourceCode: encodedCodeString },
				{ shouldReportFailure: true },
			);
		},
		async setEditorSourceCode(code: string, syncWithRemote = false) {
			// TODO: currently remote sync is not supported
			// syncWithRemote = false;

			this.editorSourceCode = code;

			// Base 64 encode the string before storing it
			const encodedCodeString = base64Encode(this.editorSourceCode);
			sessionStorage.setItem("signaloid-editor-code", encodedCodeString);
			if (syncWithRemote) {
				const authUserStore = useUserStore();
				await authUserStore.syncUserPreferenceWithRemote({
					Editor_SourceCode: encodedCodeString,
				});
			}
		},

		/*
		 * Initialise state
		 */
		drawCachedEditorPreferences(preferences: undefined | Partial<UserPreferences>) {
			if (this.mountConfig === undefined) {
				this.mountConfig = util.parseMountConfigString(preferences?.Editor_Execution_DataSources);
			}
			if (preferences?.Editor_Execution_Arguments !== undefined) {
				this.setEditorArgs(preferences.Editor_Execution_Arguments, false);
			}
			if (preferences?.Editor_Compilation_Flags !== undefined) {
				this.setEditorCompilationFlags(preferences.Editor_Compilation_Flags, false);
			}
			if (preferences?.Editor_Layout !== undefined) {
				this.setEditorLayout(preferences.Editor_Layout, false);
			}
			if (preferences?.Editor_Layout_VariableViewer !== undefined) {
				this.setVariableViewerVisibility(preferences.Editor_Layout_VariableViewer === "true", false);
			}
		},
		async initialiseEditorStateFromRemote() {
			const authUserStore = useUserStore();
			await authUserStore.cachedUserRecordRead;
			if (authUserStore.currentUserObjectFromDB === undefined) {
				this.drawCachedEditorPreferences(authUserStore.cachedCurrentUserObjectFromDB?.Preferences);
			}
			const preferencesFromDB = await authUserStore.getCurrentUserPreferences();

			if (preferencesFromDB) {
				this.coreUpdateStatus.loading = true;
				this.runArgumentsUpdateStatus.loading = true;
				this.dataSourceUpdateStatus.loading = true;

				//* Editor Source Code
				const editorCode = preferencesFromDB?.Editor_SourceCode;

				if (editorCode) {
					this.setEditorSourceCode(this.parseSourceCode(editorCode));
				} else {
					console.warn("Invalid editor code from remote. Resetting to default.");
					this.setEditorSourceCode(fallbackEditorCode);
				}

				//* Editor Theme
				let themeID = preferencesFromDB.Editor_Theme ?? this.editorThemeID;
				themeID = themeID && monacoThemes[themeID] ? themeID : fallbackEditorTheme;

				// set theme and sync if theme different to whats in db (parsing error or non existent key)
				this.setEditorTheme(themeID, themeID !== preferencesFromDB?.Editor_Theme);

				//* Editor command-line args
				const editorArgsFromDB = preferencesFromDB?.Editor_Execution_Arguments ?? "";
				// set CLI args and sync if theme different to whats in db (parsing error or non existent key)
				this.setEditorArgs(
					editorArgsFromDB,
					editorArgsFromDB !== preferencesFromDB?.Editor_Execution_Arguments,
				);
				this.runArgumentsUpdateStatus.loading = false;

				//* Editor Compilation Flags
				const editorCompilationFlagsFromDB = preferencesFromDB?.Editor_Compilation_Flags ?? "";
				this.setEditorCompilationFlags(
					editorCompilationFlagsFromDB,
					editorCompilationFlagsFromDB !== preferencesFromDB?.Editor_Compilation_Flags,
				);
				this.compilationFlagsUpdateStatus.loading = false;

				//* Editor Layout
				const editorLayoutFromDB = preferencesFromDB?.Editor_Layout ?? "vertical";
				// set editor layout and sync if theme different to whats in db (parsing error or non existent key)
				this.setEditorLayout(editorLayoutFromDB, editorLayoutFromDB !== preferencesFromDB?.Editor_Layout);

				//* Variable viewer visibility
				const variableViewerVisibilityFromDB = preferencesFromDB?.Editor_Layout_VariableViewer;
				if (variableViewerVisibilityFromDB) {
					// we got something from  the DB
					this.setVariableViewerVisibility(variableViewerVisibilityFromDB === "true", false);
				} else {
					console.log(
						`Error parsing variable viewer state.  Resetting to default - ${fallbackVariableViewerVisibility}.`,
					);
					this.setVariableViewerVisibility(fallbackVariableViewerVisibility, true);
				}

				//* Editor Core
				const editorCoreIDFromDB = preferencesFromDB?.Editor_Execution_Core;
				const coresStore = useCoresStore();
				await coresStore.drawCachedCores();
				const savedEditorCore =
					typeof editorCoreIDFromDB === "string"
						? coresStore.getCoreByID(editorCoreIDFromDB, coresStore.filteredCoresList)
						: undefined;
				if (savedEditorCore && !isRestrictedCore(savedEditorCore)) {
					this.editorCore = savedEditorCore.CoreID;
				}
				// if the cores havent been fetched from DB, wait
				await util.waitFor(() => coresStore.remoteCoresList !== undefined);
				if (editorCoreIDFromDB && typeof editorCoreIDFromDB === "string") {
					const editorCoreId = await coresStore.resetEditorCoreIfRestricted(
						editorCoreIDFromDB,
						fallbackExecutionCoreID,
					);
					this.setEditorCore(editorCoreId, true);
				} else if (
					preferencesFromDB?.Execution_DefaultCore &&
					typeof preferencesFromDB?.Execution_DefaultCore === "string"
				) {
					console.warn("Invalid editor core ID from remote. Trying to set user default...");
					const editorCoreId = await coresStore.resetEditorCoreIfRestricted(
						preferencesFromDB?.Execution_DefaultCore,
						fallbackExecutionCoreID,
					);
					this.setEditorCore(editorCoreId, true);
				} else {
					console.warn(
						"Invalid editor core ID from remote and no user default code. Resetting to global default.",
					);
					this.setEditorCore(fallbackExecutionCoreID, true);
				}
				this.coreUpdateStatus.loading = false;

				//* Editor Data Source Config
				const editorDataSourcesFromDB = preferencesFromDB?.Editor_Execution_DataSources;
				const dataSourcesStore = useDataSourcesStore();
				if (editorDataSourcesFromDB && typeof editorDataSourcesFromDB === "string") {
					// try to parse the string
					const parsedMountConfig = util.parseMountConfigString(editorDataSourcesFromDB);
					const isValidMountConfig = await dataSourcesStore.isValidMountConfig(parsedMountConfig);
					if (isValidMountConfig) {
						this.setEditorMountConfig(parsedMountConfig, false);
					} else {
						console.warn("Editor mount config in DB is invalid. Resetting to default config.");
						const validMountConfig =
							await dataSourcesStore.validateOrGetValidMountConfig(parsedMountConfig);

						this.setEditorMountConfig(validMountConfig, true);
					}
				} else {
					// no data source set in DB, set to default mount config
					console.warn("No editor mount config set in DB. Resetting to first time mount config.");
					const validMountConfig = await dataSourcesStore.validateOrGetValidMountConfig(undefined);

					this.setEditorMountConfig(validMountConfig, true);
				}
				this.dataSourceUpdateStatus.loading = false;
			} else {
				// No preferences from DB - initialize with fallback values for first-time users
				console.log("No preferences found in DB. Initializing editor with fallback values.");

				//* Editor Source Code
				this.setEditorSourceCode(fallbackEditorCode);

				//* Editor Theme
				this.setEditorTheme(fallbackEditorTheme, false);

				//* Editor Layout
				this.setEditorLayout(fallbackEditorLayout, false);

				//* Variable viewer visibility
				this.setVariableViewerVisibility(fallbackVariableViewerVisibility, false);

				//* Editor Core
				const coresStore = useCoresStore();
				await util.waitFor(() => coresStore.remoteCoresList !== undefined);
				this.setEditorCore(fallbackExecutionCoreID, false);

				//* Editor Data Source Config
				const dataSourcesStore = useDataSourcesStore();
				const validMountConfig = await dataSourcesStore.validateOrGetValidMountConfig(undefined);
				this.setEditorMountConfig(validMountConfig, false);
			}
		},
		parseSourceCode(editorCode) {
			try {
				if (editorCode === undefined || editorCode === "" || editorCode === null) {
					throw "Invalid editor code from local storage. Resetting to default.";
				}

				return base64Decode(editorCode);
			} catch (error) {
				monitoringCaptureError(error, "Validate code editor source");
				return fallbackEditorCode;
			}
		},
	},
});
